/**
 * Daily Task Page
 * Daily task check-in system with streak rewards
 */
import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { DailyTaskService, initDailyTaskService, DailyTask } from '../services/daily-task';

interface DailyTaskPageProps {
  user: UserProgress;
}

export default function DailyTaskPage({ user }: DailyTaskPageProps) {
  const [taskService, setTaskService] = useState<DailyTaskService | null>(null);
  const [progress, setProgress] = useState<any>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    initDailyTaskService(user.id);
    const service = new DailyTaskService(user.id);
    setTaskService(service);
    setProgress(service.getTodayTasks());
  }, [user.id]);

  const handleClaimReward = () => {
    if (!taskService) return;

    const reward = taskService.getTodayReward();
    setShowCelebration(true);

    setTimeout(() => {
      setShowCelebration(false);
      // Add points to user
      // storageService.addPoints(user.id, reward);
      alert(`恭喜获得 ${reward} 积分！${taskService.calculateStreakBonus() > 1 ? ' (包含连续学习奖励)' : ''}`);
    }, 2000);
  };

  if (!progress) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin text-4xl">⏳</div>
        <p className="text-gray-500 mt-4">加载中...</p>
      </div>
    );
  }

  const completionPercent = taskService?.getCompletionPercentage() || 0;
  const allCompleted = taskService?.isAllTasksCompleted() || false;
  const streakBonus = taskService?.calculateStreakBonus() || 1;

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-6 text-white mb-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">每日打卡 📅</h1>
            <p className="text-white/80 text-sm">{new Date().toLocaleDateString('zh-CN')}</p>
          </div>
          <div className="text-5xl animate-float">🔥</div>
        </div>

        {/* Streak info */}
        <div className="bg-white/20 rounded-xl p-3">
          <p className="text-sm">{taskService?.getStreakMessage()}</p>
        </div>
      </div>

      {/* Progress overview */}
      <div className="bg-white rounded-3xl p-6 shadow-soft mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-700">今日进度</h3>
          <span className="text-2xl font-bold text-orange-500">{completionPercent}%</span>
        </div>

        <div className="progress-bar mb-4">
          <div
            className="progress-bar-fill bg-gradient-to-r from-yellow-400 to-orange-500"
            style={{ width: `${completionPercent}%` }}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="bg-orange-50 rounded-xl p-3">
            <p className="text-lg font-bold text-orange-600">{progress.totalCompleted}</p>
            <p className="text-xs text-gray-500">已完成任务</p>
          </div>
          <div className="bg-yellow-50 rounded-xl p-3">
            <p className="text-lg font-bold text-yellow-600">{streakBonus > 1 ? `${Math.round((streakBonus - 1) * 100)}%` : '0%'}</p>
            <p className="text-xs text-gray-500">奖励加成</p>
          </div>
        </div>
      </div>

      {/* Task list */}
      <div className="space-y-3 mb-6">
        {progress.tasks.map((task: DailyTask) => (
          <div
            key={task.id}
            className={`bg-white rounded-xl p-4 shadow-soft transition-all ${
              task.completed ? 'border-2 border-green-400 bg-green-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{task.icon}</span>
                <div>
                  <p className="font-bold text-gray-800">{task.title}</p>
                  <p className="text-sm text-gray-500">{task.description}</p>
                </div>
              </div>

              <div className="text-right">
                {task.completed ? (
                  <div className="text-2xl animate-bounce">✅</div>
                ) : (
                  <div>
                    <p className="text-lg font-bold text-gray-700">
                      {task.current}/{task.target}
                    </p>
                    <p className="text-xs text-gray-400">+{task.reward}分</p>
                  </div>
                )}
              </div>
            </div>

            {/* Progress bar for task */}
            {!task.completed && (
              <div className="mt-3">
                <div className="progress-bar h-2">
                  <div
                    className="progress-bar-fill bg-blue-400"
                    style={{ width: `${(task.current / task.target) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Claim reward button */}
      {allCompleted && (
        <button
          onClick={handleClaimReward}
          className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all animate-pulse"
        >
          🎉 领取奖励！+{taskService?.getTodayReward()} 积分
        </button>
      )}

      {/* Tips */}
      <div className="mt-6 bg-gray-50 rounded-xl p-4 text-center">
        <p className="text-sm text-gray-500">
          💡 提示：连续学习可获得额外奖励！<br/>
          3天+20%，7天+50%
        </p>
      </div>

      {/* Celebration overlay */}
      {showCelebration && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center z-50 animate-bounce-in">
          <div className="text-center">
            <div className="text-6xl animate-bounce mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-orange-600 mb-2">太棒了！</h2>
            <p className="text-lg text-gray-600">完成今日所有任务！</p>
          </div>
        </div>
      )}
    </div>
  );
}